import { Drawer } from '@southfields-digital/mpxlive-components';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { CrossListDndProvider } from 'src/components/DndKitSortableList';
import GraphicDetails from 'src/components/GraphicDetails';
import GraphicList from 'src/components/GraphicList/GraphicList';
import Graphics from 'src/components/Graphics';
import Playlists from 'src/components/Playlists';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle as ResizeHandle,
} from 'src/components/ResizeablePanel';
import RundownDialog, { DialogTypes } from 'src/components/RundownDialog';
import ToggleButtons from 'src/components/ToggleButtons/ToggleButtons';
import ViewWindow from 'src/components/ViewWindow/ViewWindow';
import { StateType } from 'src/redux/reducers';
import { REQUEST_CREATE_PLAYLIST } from 'src/redux/reducers/playlist';
import { REQUEST_CREATE_PLAYLIST_GRAPHIC } from 'src/redux/reducers/playlistGraphic';

import styles from '../RundownDetail.module.scss';

export default function LayoutSimplified({ children }: { children?: ReactNode }) {
  const { rundownId } = useParams<{ rundownId: string }>();
  const dispatch = useDispatch();

  const { data } = useSelector((state: StateType) => state.rundown.detail);
  const { selectedId: activePlaylistId } = useSelector((state: StateType) => state.playlist.getAll);
  const { selectedPlaylistGraphic } = useSelector((state: StateType) => state.playlistGraphic);

  const guaranteedActivePlaylistId = useRef<string | null>(activePlaylistId);

  const [showGraphicDrawer, setShowGraphicDrawer] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<DialogTypes | null>(null);

  useEffect(() => {
    guaranteedActivePlaylistId.current = activePlaylistId;
  }, [activePlaylistId]);

  if (!data || !rundownId) return null;

  const handleGraphicSelected = (graphicId: string) => {
    dispatch({
      type: REQUEST_CREATE_PLAYLIST_GRAPHIC,
      payload: { playlistId: guaranteedActivePlaylistId.current, graphicId },
    });

    setShowGraphicDrawer(false);
  };

  const handleRundownDialog = (name: string) => {
    switch (showDialog) {
      case DialogTypes.PLAYLIST:
        dispatch({ type: REQUEST_CREATE_PLAYLIST, payload: { rundownId, name } });
        break;
    }
    setShowDialog(null);
  };

  const getDialogMeta = () => {
    switch (showDialog) {
      case DialogTypes.PLAYLIST:
        return { title: 'Create Playlist', subtitle: 'Enter name for the playlist' };
      default:
        return { title: '', subtitle: '' };
    }
  };

  return (
    <div className="h-full">
      <RundownDialog
        dialogTitle={getDialogMeta().title}
        dialogSubtitle={getDialogMeta().subtitle}
        isOpen={!!showDialog}
        onClose={() => setShowDialog(null)}
        onSubmit={handleRundownDialog}
      />
      <PanelGroup className={styles.Container} direction="horizontal">
        <CrossListDndProvider>
          <Panel defaultSize={25} minSize={25} id="first-column" order={1}>
            <PanelGroup direction="vertical">
              <Panel
                header="Playlists"
                icon="plus"
                onClick={() => setShowDialog(DialogTypes.PLAYLIST)}
                defaultSize={40}
              >
                <Playlists rundownId={rundownId} />
              </Panel>

              <ResizeHandle />

              <Panel
                header={
                  <ToggleButtons
                    items={[{ id: 'graphics', name: 'Graphics' }]}
                    selectedItem={{ id: 'graphics', name: 'Graphics' }}
                    onClickItem={() => setShowGraphicDrawer(true)}
                  />
                }
                canEdit={!!activePlaylistId}
                icon="plus"
                onClick={() => setShowGraphicDrawer(true)}
              >
                <Graphics />
              </Panel>
            </PanelGroup>
          </Panel>
        </CrossListDndProvider>

        <ResizeHandle />

        <Panel defaultSize={50} id="second-column" order={2}>
          <PanelGroup direction="vertical">
            <Panel className="!bg-default-surface">
              <ViewWindow name="Sandbox" src={data.sandboxUrl} liveMode={false} bgSwitch />
            </Panel>

            <ResizeHandle />

            <Panel>{selectedPlaylistGraphic && <GraphicDetails key={rundownId} />}</Panel>
          </PanelGroup>
        </Panel>

        {children}
      </PanelGroup>

      <Drawer
        isVisible={showGraphicDrawer}
        onClose={() => setShowGraphicDrawer(false)}
        header="Graphics"
        className="overflow-auto h-full pb-4"
      >
        <GraphicList rundownId={rundownId} handleGraphicSelected={handleGraphicSelected} />
      </Drawer>
    </div>
  );
}
